import React from 'react';
import { format } from 'date-fns';
import { AlertCircle, AlertTriangle, Clock, Mail } from 'lucide-react';
import { OrderStatus, PersistedOrderPayload } from '@onesunday/domain';
import { getOrderTargetDate } from '../constants';

interface OrderListProps {
  orders: PersistedOrderPayload[];
  onOrderClick: (order: PersistedOrderPayload) => void;
  selectedItems: string[];
  sortField: 'orderId' | 'customer' | 'arrival' | 'status' | 'createdAt';
  sortDirection: 'asc' | 'desc';
  onSortChange: (field: 'orderId' | 'customer' | 'arrival' | 'status' | 'createdAt') => void;
}

import { STATUS_STYLES } from '../constants';

const StatusBadge = ({ status }: { status: OrderStatus }) => {
  const styles =
    STATUS_STYLES[status] ||
    STATUS_STYLES.Completed;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${styles.bg} ${styles.text}`}
    >
      {status}
    </span>
  );
};

const SortableHeader = ({
  label,
  field,
  sortField,
  sortDirection,
  onClick,
}: {
  label: string;
  field: 'orderId' | 'customer' | 'arrival' | 'status' | 'createdAt';
  sortField: OrderListProps['sortField'];
  sortDirection: OrderListProps['sortDirection'];
  onClick: OrderListProps['onSortChange'];
}) => {
  const isActive = sortField === field;
  const arrow = isActive ? (sortDirection === 'asc' ? '^' : 'v') : null;

  return (
    <th className="px-6 py-3 font-medium">
      <button
        type="button"
        className={`flex items-center gap-1 uppercase tracking-wide text-xs font-semibold ${
          isActive ? 'text-admin-text' : 'text-admin-subtle'
        }`}
        onClick={() => onClick(field)}
      >
        {label}
        {arrow && <span className="text-[10px] leading-none">{arrow}</span>}
      </button>
    </th>
  );
};

export const OrderList: React.FC<OrderListProps> = ({
  orders,
  onOrderClick,
  selectedItems,
  sortField,
  sortDirection,
  onSortChange,
}) => {
  const now = new Date();

  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-admin-subtle bg-admin-surface rounded-xl shadow-sm border border-admin-border">
        <div className="bg-admin-surface-2 p-4 rounded-full mb-3">
          <Clock size={32} />
        </div>
        <p className="font-medium">No orders match current filters</p>
      </div>
    );
  }

  return (
    <div className="bg-admin-surface shadow-sm rounded-xl border border-admin-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-admin-muted uppercase bg-admin-surface-2 border-b border-admin-border">
            <tr>
              <SortableHeader
                label="Order #"
                field="orderId"
                sortField={sortField}
                sortDirection={sortDirection}
                onClick={onSortChange}
              />
              <SortableHeader
                label="Customer"
                field="customer"
                sortField={sortField}
                sortDirection={sortDirection}
                onClick={onSortChange}
              />
              <SortableHeader
                label="Arrival"
                field="arrival"
                sortField={sortField}
                sortDirection={sortDirection}
                onClick={onSortChange}
              />
              <SortableHeader
                label="Status"
                field="status"
                sortField={sortField}
                sortDirection={sortDirection}
                onClick={onSortChange}
              />
              <th className="px-6 py-3 font-medium w-1/3">Items</th>
              <th className="px-6 py-3 font-medium text-center">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-admin-border">
            {orders.map((order) => {
              const isComplete = order.status === 'Completed';
              const targetDate = getOrderTargetDate(order);
              const isLate = order.status === 'Late';

              const rowOpacity = isComplete ? 'opacity-60 bg-admin-surface-2' : 'opacity-100';
              const lateClass = isLate
                ? 'border-l-4 border-l-red-500 bg-red-50/30'
                : 'border-l-4 border-l-transparent';

              return (
                <tr
                  key={order.orderId}
                  onClick={() => onOrderClick(order)}
                  className={`bg-admin-surface hover:bg-admin-accent-soft cursor-pointer transition-all duration-200 ${rowOpacity} ${lateClass}`}
                >
                  <td className="px-6 py-4 font-medium text-admin-text">
                    <div className="flex items-center gap-2 flex-wrap">
                      {order.orderId}
                      {isLate && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-[#FDE2DD] text-[#A41C2C]">
                          <AlertTriangle size={10} />
                          Late
                        </span>
                      )}
                      {order.source && order.source !== 'onesunday' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-admin-surface-2 text-admin-muted">
                          {order.source}
                        </span>
                      )}
                      {order.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(order.eventDate) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-[#EFF6FF] text-[#0469C7]" title={`Event date: ${order.eventDate}`}>
                          {format(new Date(order.eventDate + 'T00:00:00'), 'd MMM')}
                        </span>
                      )}
                      {Array.isArray(order.customerEmails) && order.customerEmails.length > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-amber-50 text-amber-800"
                          title={`Sent ${order.customerEmails.length} customer email${order.customerEmails.length === 1 ? '' : 's'} (latest: ${order.customerEmails[order.customerEmails.length - 1].subject})`}
                        >
                          <Mail size={10} />
                          {order.customerEmails.length}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-admin-text">{order.customer?.name}</td>
                  <td
                    className={`px-6 py-4 font-mono ${
                      isLate ? 'text-red-700 font-bold' : 'text-admin-muted'
                    }`}
                  >
                    {format(targetDate, 'HH:mm')}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-2">
                      {(order.items || []).map((item, idx) => {
                        const isHighlighted =
                          selectedItems.length === 0 || selectedItems.includes(item.itemId);
                        return (
                          <span
                            key={idx}
                            className={`text-xs px-1.5 py-0.5 rounded border ${
                              isHighlighted
                                ? 'bg-admin-surface-2 text-admin-text border-admin-border font-medium'
                                : 'bg-transparent text-admin-subtle border-transparent'
                            }`}
                          >
                            {item.name} <span className="text-admin-subtle">x{item.quantity}</span>
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {order.customer?.notes && (
                      <div className="inline-block relative group">
                        <AlertCircle size={18} className="text-amber-500" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-32 bg-admin-text text-white text-xs p-2 rounded hidden group-hover:block z-50 shadow-lg">
                          {order.customer.notes}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
